/* Linden — toasts.

   A confirmation that does not interrupt. Adding something from a block halfway
   down a product page needs an answer, but opening the cart drawer for it
   covers the very block being used — fine for one add, wrong for three in a
   row.

   Generic on purpose: nothing here knows about carts. Anything in the theme can
   raise one without importing anything, which matters because Shopify serves
   scripts as separate bundles with no shared module graph:

     document.dispatchEvent(new CustomEvent('toast', {
       detail: { message: 'Ajouté au panier', action: { label: 'Voir', href: '/cart' } },
     }));

   Notes on the choices, since they are easy to undo by accident:

   - The live region is rendered by theme.liquid rather than created here. A
     region announced by a screen reader has to be in the document before
     content lands in it; one created and filled in the same tick is commonly
     missed entirely.
   - polite, never assertive. An assertive region interrupts whatever is being
     read, which is the wrong trade for a confirmation nobody has to act on —
     doubly so on a shop whose audience it would jar.
   - The timer pauses on hover and on focus, and any toast holding focus is kept
     until it is let go. WCAG 2.2.1 asks that a time limit be adjustable; a
     confirmation that is only ever redundant with the page may auto-dismiss,
     but not out from under someone reading or tabbing through it.
   - Same message twice in a row refreshes the toast that is already there
     instead of stacking a duplicate — three ticks say one thing, not three. */

const LIFETIME = 6000;
const MAX = 3;

const stack = () => document.querySelector('[data-toasts]');

function dismiss(toast) {
  if (!toast.isConnected) return;

  toast.classList.add('toast--leaving');

  const done = () => toast.remove();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  if (motion.matches) done();
  else toast.addEventListener('transitionend', done, { once: true });

  /* A transitionend that never fires — an interrupted transition, a hidden tab
     — would otherwise leave the toast on screen for good. */
  setTimeout(done, 400);
}

function arm(toast) {
  clearTimeout(toast.timer);

  if (toast.matches(':hover') || toast.contains(document.activeElement)) return;

  toast.timer = setTimeout(() => dismiss(toast), LIFETIME);
}

function show({ message, action } = {}) {
  const host = stack();
  if (!host || !message) return;

  const existing = [...host.children].find((node) => node.dataset.message === message);
  if (existing) {
    arm(existing);
    return existing;
  }

  const toast = document.createElement('div');
  toast.className = 'toast';
  toast.dataset.message = message;

  const text = document.createElement('p');
  text.className = 'toast__text';
  text.textContent = message;
  toast.append(text);

  if (action?.label && action?.href) {
    const link = document.createElement('a');
    link.className = 'toast__action';
    link.href = action.href;
    link.textContent = action.label;
    toast.append(link);
  }

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'toast__close';
  close.setAttribute('aria-label', host.dataset.closeLabel || 'Close');
  close.textContent = '×';
  close.addEventListener('click', () => dismiss(toast));
  toast.append(close);

  toast.addEventListener('mouseenter', () => clearTimeout(toast.timer));
  toast.addEventListener('mouseleave', () => arm(toast));
  toast.addEventListener('focusin', () => clearTimeout(toast.timer));
  toast.addEventListener('focusout', () => arm(toast));

  host.append(toast);

  while (host.children.length > MAX) dismiss(host.firstElementChild);

  /* Next frame, so the entry transition has a state to come from. */
  requestAnimationFrame(() => toast.classList.add('toast--in'));
  arm(toast);

  return toast;
}

document.addEventListener('toast', (event) => show(event.detail));
